/**
 * BranchDetailModal
 * Read-only side-sheet showing all relevant information for a given branch:
 *  - Branch basic info
 *  - Parent company
 *  - Associated owner (dueño)
 *  - Managers assigned to this branch
 */

import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Building2,
  Store,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Hash,
  User as UserIcon,
  AtSign,
  Mail,
  UserCog,
  MapPin,
  Link,
  KeyRound,
} from "lucide-react";
import { type Branch } from "@/services/branchesService";
import { type Company } from "@/services/companiesService";
import { type User } from "@/services/usersService";
import UserAvatar from "@/components/UserAvatar";
import BranchInfraStatus from "@/components/BranchInfraStatus";
import CredentialsDialog from "@/components/CredentialsDialog";

interface BranchDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  branch: Branch | null;
  company: Company | null;
  owner: User | null;
  managers: User[];
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-white/40">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-white/40 mb-0.5">{label}</p>
        <p className="text-sm text-white break-words">
          {value || <span className="text-white/30 italic">Sin dato</span>}
        </p>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
      {children}
    </p>
  );
}

export default function BranchDetailModal({
  isOpen,
  onClose,
  branch,
  company,
  owner,
  managers,
}: BranchDetailModalProps) {
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);

  if (!branch) return null;

  const createdAt = branch.created_at
    ? new Date(branch.created_at).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <>
    <CredentialsDialog
      isOpen={isCredentialsOpen}
      onClose={() => setIsCredentialsOpen(false)}
      branchId={branch.id}
      branchName={branch.name}
    />
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white flex-shrink-0">
              <Store className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {branch.name}
              </SheetTitle>
              <p className="text-sm text-white/50 mt-0.5">{branch.company_name || "Sin empresa"}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge
                  className={
                    branch.is_active
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                      : "bg-red-500/20 text-red-400 border-red-500/30"
                  }
                >
                  {branch.is_active ? "Activa" : "Inactiva"}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsCredentialsOpen(true)}
                  className="h-6 px-2 text-[10px] gap-1 text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/10 border border-amber-500/20"
                >
                  <KeyRound className="h-3 w-3" />
                  Ver credenciales
                </Button>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Scrollable body */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Sucursal — datos básicos */}
            <section className="space-y-3">
              <SectionTitle>
                <Store className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Información de la sucursal
              </SectionTitle>
              <InfoRow
                icon={<Hash className="h-4 w-4" />}
                label="ID"
                value={<span className="font-mono text-xs">{branch.id}</span>}
              />
              <InfoRow icon={<CalendarDays className="h-4 w-4" />} label="Registrada el" value={createdAt} />
              {branch.address && (
                <InfoRow icon={<MapPin className="h-4 w-4" />} label="Dirección" value={branch.address} />
              )}
              {branch.backend_url && (
                <InfoRow
                  icon={<Link className="h-4 w-4" />}
                  label="URL del backend"
                  value={
                    <a
                      href={branch.backend_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-xs text-sky-400 hover:underline break-all"
                    >
                      {branch.backend_url}
                    </a>
                  }
                />
              )}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-white/40">
                  {branch.is_active ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">Estado</p>
                  <Badge
                    className={
                      branch.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30"
                    }
                  >
                    {branch.is_active ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
              </div>
            </section>

            <Separator className="bg-white/10" />

            {/* Infraestructura */}
            <section>
              <SectionTitle>
                <Store className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Infraestructura
              </SectionTitle>
              <BranchInfraStatus branchId={branch.id} />
            </section>

            <Separator className="bg-white/10" />

            {/* Empresa */}
            <section className="space-y-3">
              <SectionTitle>
                <Building2 className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Empresa
              </SectionTitle>
              {company ? (
                <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10 space-y-2">
                  <div className="flex items-center gap-3">
                    <UserAvatar role="company" size="sm" icon={<Building2 className="h-4 w-4" />} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{company.name}</p>
                      <Badge
                        className={
                          company.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mt-1"
                            : "bg-red-500/20 text-red-400 border-red-500/30 mt-1"
                        }
                      >
                        {company.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white/30 italic">Sin empresa asignada</p>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Dueño */}
            <section className="space-y-3">
              <SectionTitle>
                <UserIcon className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Dueño
              </SectionTitle>
              {owner ? (
                <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10 space-y-3">
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      initials={(((owner.first_name?.[0] ?? '') + (owner.last_name?.[0] ?? '')).toUpperCase()) || owner.username[0].toUpperCase()}
                      role="owner"
                      size="md"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {[owner.first_name, owner.last_name].filter(Boolean).join(" ") || owner.username}
                      </p>
                      <Badge
                        className={
                          owner.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mt-1"
                            : "bg-red-500/20 text-red-400 border-red-500/30 mt-1"
                        }
                      >
                        {owner.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                  </div>
                  <InfoRow icon={<AtSign className="h-4 w-4" />} label="Usuario" value={owner.username} />
                  <InfoRow icon={<Mail className="h-4 w-4" />} label="Correo" value={owner.email} />
                </div>
              ) : (
                <p className="text-sm text-white/30 italic">Sin dueño asignado</p>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Gerentes */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <SectionTitle>
                  <UserCog className="inline-block h-3 w-3 mr-1 mb-0.5" />
                  Gerentes asignados
                </SectionTitle>
                {managers.length > 0 && (
                  <span className="text-xs text-white/40 -mt-3">{managers.length} registrados</span>
                )}
              </div>
              {managers.length === 0 ? (
                <p className="text-sm text-white/30 italic">Sin gerentes asignados</p>
              ) : (
                <div className="space-y-2">
                  {managers.map((mgr) => (
                    <div
                      key={mgr.id}
                      className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 border border-white/10"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <UserAvatar
                          initials={(((mgr.first_name?.[0] ?? '') + (mgr.last_name?.[0] ?? '')).toUpperCase()) || mgr.username[0].toUpperCase()}
                          role="manager"
                          size="sm"
                        />
                        <div className="min-w-0">
                          <p className="text-sm text-white font-medium truncate">
                            {[mgr.first_name, mgr.last_name].filter(Boolean).join(" ") || mgr.username}
                          </p>
                          <p className="text-xs text-white/40 truncate">@{mgr.username}</p>
                        </div>
                      </div>
                      <Badge
                        className={
                          mgr.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 ml-2 flex-shrink-0"
                            : "bg-red-500/20 text-red-400 border-red-500/30 ml-2 flex-shrink-0"
                        }
                      >
                        {mgr.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </section>

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
    </>
  );
}
